/**
 * ReportStepTabs - 报告步骤页签组件
 * 用于切换分析思路和分析报告两个步骤
 * 支持通过 stepsConfig 自定义步骤配置
 */
import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import CommonBubble from '@home/components/CommonBubble';
import SvgIcon from '@home/components/SvgIcon';
import { IReportStepTabsProps, TReportStepType } from '../types';
import { REPORT_STEP_TABS_CONFIG } from '../constants';
import './ReportStepTabs.scss';

export const ReportStepTabs: React.FC<IReportStepTabsProps> = ({
  activeStep,
  isReportDisabled,
  onStepChange,
  onClose,
  showCloseButton = true,
  stepsConfig,
  disabledTip,
  disabledTipEn,
  className,
}) => {
  const { locale, formatMessage } = useIntl();
  const isEn = locale === 'en-US';

  // 使用自定义配置或默认配置
  const tabs = stepsConfig || REPORT_STEP_TABS_CONFIG;

  const handleStepClick = (step: TReportStepType, index: number, e: React.MouseEvent) => {
    // 阻止拖拽冒泡
    e.stopPropagation();
    // 如果是第二步且被禁用，则不响应点击
    if (index === 1 && isReportDisabled) {
      return;
    }
    onStepChange(step);
  };

  // 禁用时的提示文案
  const defaultDisabledTip = isEn
    ? (disabledTipEn || formatMessage({ id: 'dba_report_step_disabled_tip' }))
    : (disabledTip || formatMessage({ id: 'dba_report_step_disabled_tip' }));

  return (
    <div className={classNames('dba-report-step-tabs', className)}>
      <div className="dba-report-step-tabs__list">
        {tabs.map((tab, index) => (
          <CommonBubble
            overlayClassName="dba-report-file-selector-overlay"
            placement="right"
            key={tab.value}
            dark
            closeOnScroll
            destroyOnClose
            content={
              isReportDisabled &&
              index === 1 && (
                <div>
                  {defaultDisabledTip}
                </div>
              )
            }
          >
            <div
              className={classNames('dba-report-step-tabs__item', {
                'dba-report-step-tabs__item--active': activeStep === tab.value,
                'dba-report-step-tabs__item--disabled':
                  index === 1 && isReportDisabled,
                'dba-report-step-tabs__item--with-icon': Boolean(tab.icon),
              })}
              onMouseDown={(e) => handleStepClick(tab.value, index, e)}
            >
              {tab.icon ? (
                <span className="dba-report-step-tabs__item-icon">{tab.icon}</span>
              ) : null}
              <span className="dba-report-step-tabs__item-label">
                {(tab.showStepIndex !== false ? `${index + 1}. ` : '')}
                {isEn ? tab.labelEn : tab.label}
              </span>
            </div>
          </CommonBubble>
        ))}
      </div>
      {showCloseButton && onClose && (
        <div
          onMouseDown={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <SvgIcon type="clonse" />
        </div>
      )}
    </div>
  );
};

export default ReportStepTabs;
